import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useAuthContext } from '@/features/auth/context'
import {
  createDecoderPurposeSync,
  mayNeedDecoderPurposeSync,
} from '@/features/config/pages/StationTableView/decoder-purpose-sync'
import { getApiErrorMessage } from '@/helpers/notify'
import useTranslation from '@/hooks/useTranslation'

/** What the sync needs to know about a station saved from a form. */
export interface StationDecoderFields {
  /** 1 Non Irrigation, 2 Irrigation. */
  stationType: number
  /** The form's decoder option. */
  decoder?: {
    address?: number | string | null
    index?: number | string | null
    twiId?: number | string | null
    decoderType?: string | null
  } | null
  description?: string
}

const toSyncStation = ({ stationType, decoder, description }: StationDecoderFields) => ({
  stationType,
  decoderType: decoder?.decoderType,
  decoderAddress:
    decoder?.address != null ? `${decoder.address}-${Number(decoder.index) || 0}` : '',
  twiId: decoder?.twiId != null ? Number(decoder.twiId) : null,
  description,
})

/**
 * SCRUM-1145 — the station Edit form's half of the Type ↔ decoder sync, which
 * until now only the station grid had. See `decoder-purpose-sync.ts`.
 */
const useStationDecoderSync = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { interfaceId } = useAuthContext()
  const [isSyncingDecoder, setIsSyncingDecoder] = useState(false)

  const newSync = () =>
    createDecoderPurposeSync({
      interfaceId,
      queryClient,
      notFoundMessage: t('stationsDecoderSyncNotFound'),
      unknownPurposeMessage: t('stationsDecoderSyncUnknownPurpose'),
      writeFailedMessage: t('stationsDecoderSyncWriteFailed'),
    })

  /**
   * An edit: the decoder first, then the station. A decoder that cannot be
   * written stops the save with its reason; a station the server refuses puts
   * the decoder back. The station write reports its own errors.
   */
  const saveWithDecoder = async (
    fields: StationDecoderFields,
    writeStation: () => Promise<unknown>,
  ) => {
    const station = toSyncStation(fields)
    if (!mayNeedDecoderPurposeSync(station)) {
      await writeStation().catch((error) =>
        console.error('Station save failed:', error),
      )
      return
    }

    let reachedStation = false
    setIsSyncingDecoder(true)
    try {
      await newSync().withStation(station, () => {
        reachedStation = true
        return writeStation()
      })
    } catch (error) {
      console.error('Station save with decoder sync failed:', error)
      if (!reachedStation) toast.error(getApiErrorMessage(error))
    } finally {
      setIsSyncingDecoder(false)
    }
  }

  /**
   * A create: the station is already written, so a decoder that could not
   * follow is reported rather than undoing it. The controller list flags the
   * pair until they agree.
   */
  const syncAfterCreate = async (fields: StationDecoderFields) => {
    const station = toSyncStation(fields)
    if (!mayNeedDecoderPurposeSync(station)) return
    setIsSyncingDecoder(true)
    try {
      await newSync().apply(station)
    } catch (error) {
      console.error('Station decoder purpose sync failed:', error)
      toast.error(
        `${t('stationsDecoderSyncFailed')} — ${station.description || station.decoderAddress}`,
      )
    } finally {
      setIsSyncingDecoder(false)
    }
  }

  return { saveWithDecoder, syncAfterCreate, isSyncingDecoder }
}

export default useStationDecoderSync
